import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Alert, Button, List, Typography, message } from 'antd';
import { FileAddOutlined, UploadOutlined } from '@ant-design/icons';
import { api } from '../api/http';

const MAX_BYTES = 5 * 1024 * 1024;

const ATTACHMENT_CONFIGS = [
  {
    attachmentType: 'PHOTO',
    title: 'Attachments',
    description: 'Optional photos for this submitted record. Upload up to 3 JPEG, PNG, or WebP images, 5 MB each.',
    maxFiles: 3,
    allowed: ['image/jpeg', 'image/png', 'image/webp'],
    fileAccept: 'image/jpeg,image/png,image/webp',
    cameraAccept: 'image/jpeg,image/png,image/webp',
    uploadButton: 'Photo library',
    cameraButton: 'Take photo',
    queuedMessage: (count) => `${count} image(s) queued.`,
    queuedAlert: (count) => `${count} image(s) queued. Save record to upload.`,
    limitMessage: 'Image limit reached (3/3).',
    maxSizeMessage: 'Each image must be 5 MB or smaller.',
    unsupportedFormatMessage: 'This photo format is not supported yet. Please choose or take a JPEG, PNG, or WebP image. On iPhone, use Most Compatible/JPEG if needed.',
    onlyAllowedMessage: 'Only JPEG, PNG, and WebP are allowed.',
    fallbackDownloadName: 'image',
  },
  {
    attachmentType: 'EXTERNAL_SUBMITTED_FORM',
    title: 'External submitted form',
    description: 'Upload up to 3 completed external PDF, JPEG, PNG, or WebP form pages/files, 5 MB each.',
    readOnlyDescription: 'Completed external customer form. Use View or Download to review the submitted form.',
    maxFiles: 3,
    allowed: ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'],
    fileAccept: 'application/pdf,image/jpeg,image/png,image/webp',
    cameraAccept: 'image/jpeg,image/png,image/webp',
    uploadButton: 'Upload file',
    cameraButton: 'Take photo',
    queuedMessage: (count) => `${count} external submitted-form file(s) queued.`,
    queuedAlert: (count) => `${count} external submitted-form file(s) queued. Save record to upload.`,
    limitMessage: 'External submitted-form upload limit reached (3/3).',
    maxSizeMessage: 'External submitted-form upload must be 5 MB or smaller.',
    unsupportedFormatMessage: 'This file format is not supported. Please upload a PDF, JPEG, PNG, or WebP file.',
    onlyAllowedMessage: 'Only PDF, JPEG, PNG, and WebP are allowed.',
    fallbackDownloadName: 'external-submitted-form',
  },
];

function AttachmentPanel({ config, recordType, recordId, readOnly, onUploadFailed, registerHandle, variant, hideWhenEmpty, onLoaded }) {
  const useUploadSection = variant === 'uploadSection';
  const useReadOnlySection = variant === 'readOnlySection';
  const useCompactReadOnlySection = variant === 'compactReadOnlySection';
  const enhancedUpload = useUploadSection || useReadOnlySection || useCompactReadOnlySection;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const pickerRef = useRef(null);
  const cameraRef = useRef(null);

  const viewAttachment = useCallback(async (item) => {
    try {
      const { data } = await api.getBlob(item.viewPath);
      if (!data) throw new Error('View failed');
      const objectUrl = URL.createObjectURL(data);
      window.open(objectUrl, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    } catch (e) {
      message.error(e?.message || 'Unable to view attachment.');
    }
  }, []);

  const downloadAttachment = useCallback(async (item) => {
    try {
      const { data } = await api.getBlob(item.downloadPath);
      if (!data) throw new Error('Download failed');
      const objectUrl = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = item.originalFilename || config.fallbackDownloadName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    } catch (e) {
      message.error(e?.message || 'Unable to download attachment.');
    }
  }, [config.fallbackDownloadName]);

  const currentCount = useMemo(() => items.length + pendingFiles.length, [items.length, pendingFiles.length]);

  const load = useCallback(async (rid = recordId, { silent = false } = {}) => {
    if (!rid) {
      setHasLoaded(true);
      onLoaded?.([]);
      return { ok: true, attachments: [] };
    }
    try {
      const { data } = await api.get(`/api/attachments?recordType=${encodeURIComponent(recordType)}&recordId=${rid}&attachmentType=${config.attachmentType}`);
      const attachments = data?.attachments || [];
      setItems(attachments);
      setHasLoaded(true);
      onLoaded?.(attachments);
      return { ok: true, attachments };
    } catch (e) {
      const backendMessage = e?.response?.data?.message || e?.message || 'Unable to load attachments.';
      if (!silent) message.error(backendMessage);
      return { ok: false, message: backendMessage, error: e };
    }
  }, [config.attachmentType, onLoaded, recordId, recordType]);

  React.useEffect(() => {
    load(undefined, { silent: true }).catch(() => {});
  }, [load]);

  const validateFile = useCallback((file) => {
    const type = (file.type || '').toLowerCase();
    const name = (file.name || '').toLowerCase();
    if (type === 'image/heic' || type === 'image/heif' || name.endsWith('.heic') || name.endsWith('.heif')) return config.unsupportedFormatMessage;
    if (type && type !== 'application/octet-stream' && type !== 'image/jpg' && !config.allowed.includes(type)) return config.onlyAllowedMessage;
    if (file.size > MAX_BYTES) return config.maxSizeMessage;
    if (currentCount >= config.maxFiles) return `No more files allowed for ${config.title.toLowerCase()}.`;
    return null;
  }, [config, currentCount]);

  const pickFiles = (e) => {
    const chosen = Array.from(e.target.files || []);
    if (!chosen.length) return;
    const accepted = [];
    for (const f of chosen) {
      const err = validateFile(f);
      if (err) {
        message.error(err);
        continue;
      }
      accepted.push(f);
      if (items.length + pendingFiles.length + accepted.length >= config.maxFiles) break;
    }
    if (accepted.length) {
      setPendingFiles((prev) => [...prev, ...accepted].slice(0, config.maxFiles - items.length));
      message.success(config.queuedMessage(accepted.length));
    }
    e.target.value = '';
  };

  const flushUploads = useCallback(async (targetRecordId) => {
    if (!pendingFiles.length) return { ok: true, uploaded: 0 };
    try {
      setLoading(true);
      const snapshot = [...pendingFiles];
      const fd = new FormData();
      fd.append('recordType', recordType);
      fd.append('recordId', String(targetRecordId));
      fd.append('attachmentType', config.attachmentType);
      snapshot.forEach((file) => fd.append('files', file));
      await api.post('/api/attachments', fd);
      setPendingFiles([]);
      const reload = await load(targetRecordId);
      if (!reload.ok) {
        return { ok: false, uploaded: snapshot.length, message: reload.message, stage: 'list', attachmentType: config.attachmentType };
      }
      return { ok: true, uploaded: snapshot.length, attachmentType: config.attachmentType };
    } catch (e) {
      const backendMessage = e?.response?.data?.message || e?.message || 'Upload failed.';
      onUploadFailed?.(backendMessage, config.attachmentType);
      message.error(backendMessage);
      return { ok: false, error: e, message: backendMessage, attachmentType: config.attachmentType };
    } finally {
      setLoading(false);
    }
  }, [config.attachmentType, load, onUploadFailed, pendingFiles, recordType]);

  const emptyText = readOnly
    ? 'No attachments available for this section.'
    : 'Choose a file or take a photo to add it here before submitting.';

  React.useEffect(() => {
    registerHandle(config.attachmentType, {
      flushUploads,
      hasPending: () => pendingFiles.length > 0,
      getPendingFiles: () => [...pendingFiles],
    });
    return () => registerHandle(config.attachmentType, null);
  }, [config.attachmentType, flushUploads, pendingFiles, registerHandle]);

  if (readOnly && hideWhenEmpty && hasLoaded && items.length === 0) return null;

  return (
    <div className={enhancedUpload ? `record-attachments-panel record-attachments-panel-${config.attachmentType.toLowerCase().replace(/_/g, '-')} ${useUploadSection ? 'record-attachments-panel-section' : ''} ${useReadOnlySection ? 'record-attachments-panel-readonly-section' : ''} ${useCompactReadOnlySection ? 'record-attachments-panel-compact-readonly-section' : ''}` : undefined} style={enhancedUpload ? undefined : { marginTop: 16 }}>
      {enhancedUpload && !useCompactReadOnlySection && (
        <div className="record-attachments-header">
          {useUploadSection && <div className="record-attachments-icon" aria-hidden="true"><FileAddOutlined /></div>}
          <div>
            <div className="record-attachments-title-row">
              <Typography.Title level={5}>{config.title}</Typography.Title>
              {useUploadSection && !readOnly && config.attachmentType === 'EXTERNAL_SUBMITTED_FORM' && (
                <span className="record-attachments-required-badge">Required</span>
              )}
            </div>
            {variant !== 'readOnlySection' && (
              <Typography.Paragraph type="secondary" className="record-attachments-description">
                {readOnly && config.readOnlyDescription ? config.readOnlyDescription : config.description}
              </Typography.Paragraph>
            )}
          </div>
        </div>
      )}
      {!enhancedUpload && (
        <>
          <Typography.Title level={5}>{config.title}</Typography.Title>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
            {readOnly && config.readOnlyDescription ? config.readOnlyDescription : config.description}
          </Typography.Paragraph>
        </>
      )}
      {!readOnly && (
        <>
          <input ref={pickerRef} type="file" accept={config.fileAccept} multiple={config.maxFiles > 1} onChange={pickFiles} style={{ display: 'none' }} />
          <input ref={cameraRef} type="file" accept={config.cameraAccept} capture="environment" onChange={pickFiles} style={{ display: 'none' }} />
          <div className={enhancedUpload ? 'record-attachments-actions' : undefined} style={enhancedUpload ? undefined : { display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button className={enhancedUpload ? 'dashboard-secondary-action-button record-attachments-action-button' : undefined} icon={<UploadOutlined />} loading={loading} disabled={currentCount >= config.maxFiles} onClick={() => pickerRef.current?.click()}>
              {config.uploadButton}
            </Button>
            <Button className={enhancedUpload ? 'dashboard-secondary-action-button record-attachments-action-button' : undefined} loading={loading} disabled={currentCount >= config.maxFiles} onClick={() => cameraRef.current?.click()}>
              {config.cameraButton}
            </Button>
          </div>
        </>
      )}
      {!recordId && pendingFiles.length > 0 && <Alert className={enhancedUpload ? 'record-attachments-alert' : undefined} type="info" showIcon message={config.queuedAlert(pendingFiles.length)} style={enhancedUpload ? undefined : { marginTop: 8 }} />}
      {currentCount >= config.maxFiles && <Alert className={enhancedUpload ? 'record-attachments-alert' : undefined} type="info" showIcon message={config.limitMessage} style={enhancedUpload ? undefined : { marginTop: 8 }} />}
      <List
        className={enhancedUpload ? 'record-attachments-list' : undefined}
        style={enhancedUpload ? undefined : { marginTop: 8 }}
        locale={enhancedUpload ? { emptyText: <div className="record-attachments-empty"><FileAddOutlined /><span>{readOnly ? 'No attachments' : 'No files queued yet'}</span><small>{emptyText}</small></div> } : undefined}
        dataSource={[...pendingFiles.map((f, i) => ({ id: `p-${i}`, pending: true, originalFilename: f.name, mimeType: f.type, fileSizeBytes: f.size })), ...items]}
        renderItem={(it) => {
          if ((useReadOnlySection || useCompactReadOnlySection) && !it.pending) {
            return (
              <List.Item>
                <div className="record-attachments-file-row">
                  <List.Item.Meta title={it.originalFilename} description={`${it.mimeType || it.contentType || 'file'} • ${Math.round((it.fileSizeBytes || 0) / 1024)} KB`} />
                  <div className="record-attachments-file-actions-row" aria-label={`Actions for ${it.originalFilename || 'attachment'}`}>
                    <Button className="dashboard-secondary-action-button record-attachments-file-action" type="default" onClick={() => viewAttachment(it)}>View</Button>
                    <Button className="dashboard-secondary-action-button record-attachments-file-action" type="default" onClick={() => downloadAttachment(it)}>Download</Button>
                  </div>
                </div>
              </List.Item>
            );
          }

          const actions = it.pending
            ? [<span key="q" className="record-attachments-queued-label">Queued</span>]
            : [
                <Button key="view" type="link" onClick={() => viewAttachment(it)}>View</Button>,
                <Button key="download" type="link" onClick={() => downloadAttachment(it)}>Download</Button>,
              ];
          return (
            <List.Item actions={actions}>
              <List.Item.Meta title={it.originalFilename} description={`${it.mimeType || it.contentType || 'file'} • ${Math.round((it.fileSizeBytes || 0) / 1024)} KB`} />
            </List.Item>
          );
        }}
      />
    </div>
  );
}

const RecordAttachments = forwardRef(function RecordAttachments({ recordType, recordId, readOnly, onUploadFailed, attachmentTypes = ['PHOTO', 'EXTERNAL_SUBMITTED_FORM'], variant, hideWhenEmpty = false, onLoaded }, ref) {
  const handlesRef = useRef({});

  const registerHandle = useCallback((attachmentType, handle) => {
    if (handle) handlesRef.current[attachmentType] = handle;
    else delete handlesRef.current[attachmentType];
  }, []);

  useImperativeHandle(ref, () => ({
    flushUploads: async (targetRecordId, attachmentType = null) => {
      const handles = attachmentType ? [handlesRef.current[attachmentType]].filter(Boolean) : Object.values(handlesRef.current);
      const pendingHandles = handles.filter((handle) => handle.hasPending());
      if (!pendingHandles.length) return { ok: true, uploaded: 0 };
      let uploaded = 0;
      for (const handle of pendingHandles) {
        const result = await handle.flushUploads(targetRecordId);
        if (!result.ok) return { ...result, uploaded };
        uploaded += result.uploaded || 0;
      }
      return { ok: true, uploaded };
    },
    hasPending: (attachmentType) => {
      if (attachmentType) return Boolean(handlesRef.current[attachmentType]?.hasPending?.());
      return Object.values(handlesRef.current).some((handle) => handle.hasPending());
    },
    getPendingFiles: (attachmentType) => {
      if (attachmentType) return handlesRef.current[attachmentType]?.getPendingFiles?.() || [];
      return Object.values(handlesRef.current).flatMap((handle) => handle.getPendingFiles?.() || []);
    },
  }), []);

  const orderedAttachmentConfigs = attachmentTypes
    .map((attachmentType) => ATTACHMENT_CONFIGS.find((config) => config.attachmentType === attachmentType))
    .filter(Boolean);

  const panels = orderedAttachmentConfigs.map((config) => (
        <AttachmentPanel
          key={config.attachmentType}
          config={config}
          recordType={recordType}
          recordId={recordId}
          readOnly={readOnly}
          onUploadFailed={onUploadFailed}
          registerHandle={registerHandle}
          variant={variant}
          hideWhenEmpty={hideWhenEmpty}
          onLoaded={onLoaded}
        />
      ));

  if (variant === 'uploadSection' || variant === 'readOnlySection' || variant === 'compactReadOnlySection') {
    return (
      <section
        className={variant === 'uploadSection' ? 'record-attachments-upload-section' : `record-attachments-readonly-section${variant === 'compactReadOnlySection' ? ' record-attachments-compact-readonly-section' : ''}`}
        aria-label={variant === 'uploadSection' ? 'External checklist uploads' : 'Record attachments'}
      >
        {panels}
      </section>
    );
  }

  return <>{panels}</>;
});

export default RecordAttachments;
